import { Button, Icon, TextCaption } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';

import { summarizeSolution } from '../../components/table/node-property-panel/ai-action-assist/action-assist-model';
import { ConfigPanel } from '../../components/table/src/config-panel';
import { tableStore } from '../../stores/table-store';
import { useTopUnderBuildBar } from '../build-panel-position';
import { useSolutionHealth } from '../use-solution-health';
import { buildCanvasEdges } from './canvas-edges-model';
import { buildCanvasAgentNodes } from './canvas-view-model';
import { agentFacts, routeLine, type AgentRoute } from './explain-agent-model';

import './explain-agent-panel.scss';

/**
 * "Explain this agent" (the canvas card's ✦): what an agent does and its
 * steps, in plain words (AI), beside what can be counted — how customers get
 * to it, where it goes next, and what the health check found on its nodes.
 * Nothing here changes the solution.
 */

interface Explanation {
  summary: string;
  steps: string[];
}

/** One explanation per agent and version of its nodes, for the session. */
const cache = new Map<string, Explanation>();

const requestExplanation = async (body: {
  agentName: string;
  nodes: string;
  incoming: string[];
  outgoing: string[];
}): Promise<Explanation> => {
  const response = await fetch('/ai/agents/explain', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const json = (await response.json().catch(() => null)) as
    | (Explanation & { detail?: string; error?: string })
    | null;
  if (!response.ok || !json) {
    throw new Error(json?.detail ?? json?.error ?? response.statusText);
  }
  return json;
};

const Routes: FC<{ routes: AgentRoute[]; empty: string }> = ({
  routes,
  empty,
}) =>
  routes.length ? (
    <ul className="c-explain-agent__routes">
      {routes.map((r) => (
        <li key={r.agentId}>
          <button
            type="button"
            className="c-explain-agent__route"
            title={`Explain ${r.agentTitle}`}
            onClick={() => tableStore.setExplainAgentId(r.agentId)}
          >
            <span
              className={`c-explain-agent__kind c-explain-agent__kind--${
                r.kinds.includes('intent')
                  ? 'intent'
                  : r.kinds.includes('flow')
                    ? 'flow'
                    : 'error'
              }`}
              aria-hidden="true"
            />
            {routeLine(r)}
          </button>
        </li>
      ))}
    </ul>
  ) : (
    <p className="c-explain-agent__none">{empty}</p>
  );

export const ExplainAgentPanel: FC = observer(() => {
  const top = useTopUnderBuildBar();
  const agentId = tableStore.explainAgentId;
  const rows = tableStore.getAllSortedRows();
  const issues = useSolutionHealth();
  const agents = buildCanvasAgentNodes(rows);
  const agent = agents.find((a) => a.id === agentId);
  const titles = new Map(agents.map((a) => [a.id, a.title]));
  const facts = agentId
    ? agentFacts({
        rows,
        agentId,
        edges: buildCanvasEdges(rows),
        titles,
        issues,
      })
    : null;

  const nodes = facts ? summarizeSolution(facts.members, null) : '';
  const key = `${agentId}:${nodes.length}:${nodes.slice(0, 200)}`;
  const [explanation, setExplanation] = useState<Explanation | null>(
    () => cache.get(key) ?? null
  );
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!agent || !facts || !facts.members.length) return undefined;
    const hit = cache.get(key);
    if (hit) {
      setExplanation(hit);
      setProblem(null);
      return undefined;
    }
    let cancelled = false;
    setExplanation(null);
    setProblem(null);
    setLoading(true);
    requestExplanation({
      agentName: agent.title,
      nodes,
      incoming: facts.incoming.map(routeLine),
      outgoing: facts.outgoing.map(routeLine),
    })
      .then((result) => {
        if (cancelled) return;
        cache.set(key, result);
        setExplanation(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setProblem(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `key` stands for the agent and its nodes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  if (!agent || !facts) return null;
  const close = () => tableStore.setExplainAgentId(null);

  return (
    <div
      className="c-build-actions c-explain-agent"
      style={{ top }}
      data-testid="bobcat-explain-agent-panel"
    >
      <ConfigPanel title={agent.title} onClose={close}>
        <TextCaption variant="secondary">
          {facts.members.length} node{facts.members.length === 1 ? '' : 's'}
        </TextCaption>

        <section className="c-explain-agent__sec">
          <span className="c-build-actions__group">What it does</span>
          {!facts.members.length ? (
            <p className="c-explain-agent__none">
              No nodes yet — add some and it can be explained.
            </p>
          ) : loading ? (
            <p className="c-explain-agent__loading" role="status">
              <Icon name="stars" size="xsmall" />
              Reading the agent’s nodes…
            </p>
          ) : problem ? (
            <p className="c-explain-agent__problem" role="alert">
              Couldn’t explain it: {problem}{' '}
              <Button
                size="small"
                variant="ghost"
                onClick={() => setAttempt((n) => n + 1)}
              >
                Try again
              </Button>
            </p>
          ) : explanation ? (
            <>
              <p className="c-explain-agent__summary">{explanation.summary}</p>
              {explanation.steps.length > 0 && (
                <ol className="c-explain-agent__steps">
                  {explanation.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              )}
            </>
          ) : null}
        </section>

        <section className="c-explain-agent__sec">
          <span className="c-build-actions__group">
            How customers get here · {facts.incoming.length}
          </span>
          <Routes
            routes={facts.incoming}
            empty="Nothing routes here yet — no agent sends customers to it."
          />
        </section>

        <section className="c-explain-agent__sec">
          <span className="c-build-actions__group">
            Where it goes next · {facts.outgoing.length}
          </span>
          <Routes
            routes={facts.outgoing}
            empty="It doesn’t hand off to another agent."
          />
        </section>

        <section className="c-explain-agent__sec">
          <span className="c-build-actions__group">
            Worth checking · {facts.issues.length}
          </span>
          {facts.issues.length ? (
            <ul className="c-explain-agent__issues">
              {facts.issues.map((issue) => (
                <li
                  key={issue.id}
                  className={`c-explain-agent__issue c-explain-agent__issue--${issue.severity}`}
                >
                  <span className="c-explain-agent__issue-title">
                    {issue.title}
                  </span>
                  <span className="c-explain-agent__issue-detail">
                    {issue.detail}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="c-explain-agent__none">
              The health check found nothing on this agent’s nodes.
            </p>
          )}
        </section>
      </ConfigPanel>
    </div>
  );
});
